import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const countries = [
  {
    code: "AU",
    iso3: "AUS",
    name: "Australia",
    mobileCode: "+61",
    placeholder: "412 345 678",
  },
  {
    code: "CA",
    iso3: "CAN",
    name: "Canada",
    mobileCode: "+1",
    placeholder: "416 555 0123",
  },
  {
    code: "KH",
    iso3: "KHM",
    name: "Cambodia",
    mobileCode: "+855",
    placeholder: "12 345 678",
  },
  {
    code: "CN",
    iso3: "CHN",
    name: "China",
    mobileCode: "+86",
    placeholder: "138 0013 8000",
  },
  {
    code: "FR",
    iso3: "FRA",
    name: "France",
    mobileCode: "+33",
    placeholder: "6 12 34 56 78",
  },
  {
    code: "DE",
    iso3: "DEU",
    name: "Germany",
    mobileCode: "+49",
    placeholder: "151 1234 5678",
  },
  {
    code: "HK",
    iso3: "HKG",
    name: "Hong Kong",
    mobileCode: "+852",
    placeholder: "9123 4567",
  },
  {
    code: "ID",
    iso3: "IDN",
    name: "Indonesia",
    mobileCode: "+62",
    placeholder: "812 3456 7890",
  },
  {
    code: "JP",
    iso3: "JPN",
    name: "Japan",
    mobileCode: "+81",
    placeholder: "90 1234 5678",
  },
  {
    code: "MY",
    iso3: "MYS",
    name: "Malaysia",
    mobileCode: "+60",
    placeholder: "12 345 6789",
  },
  {
    code: "NZ",
    iso3: "NZL",
    name: "New Zealand",
    mobileCode: "+64",
    placeholder: "21 123 456",
  },
  {
    code: "PH",
    iso3: "PHL",
    name: "Philippines",
    mobileCode: "+63",
    placeholder: "905 123 4567",
  },
  {
    code: "SG",
    iso3: "SGP",
    name: "Singapore",
    mobileCode: "+65",
    placeholder: "9123 4567",
  },
  {
    code: "KR",
    iso3: "KOR",
    name: "Korea, Republic of",
    mobileCode: "+82",
    placeholder: "10 1234 5678",
  },
  {
    code: "ES",
    iso3: "ESP",
    name: "Spain",
    mobileCode: "+34",
    placeholder: "612 34 56 78",
  },
  {
    code: "TW",
    iso3: "TWN",
    name: "Taiwan",
    mobileCode: "+886",
    placeholder: "912 345 678",
  },
  {
    code: "TH",
    iso3: "THA",
    name: "Thailand",
    mobileCode: "+66",
    placeholder: "81 234 5678",
  },
  {
    code: "GB",
    iso3: "GBR",
    name: "United Kingdom",
    mobileCode: "+44",
    placeholder: "7911 123456",
  },
  {
    code: "US",
    iso3: "USA",
    name: "United States",
    mobileCode: "+1",
    placeholder: "202 555 0123",
  },
];

export default function CountrySelectDialog({
  open,
  onOpenChange,
  onSelect,
  selectedCountry,
  showMobileCode = true, // 전화번호 코드 표시 여부
  useIso3 = false, // 3자리 ISO 코드 사용 여부
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;

    const query = searchQuery.toLowerCase();
    return countries.filter(
      (country) =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query) ||
        country.iso3.toLowerCase().includes(query) ||
        (showMobileCode && country.mobileCode.includes(query))
    );
  }, [searchQuery, showMobileCode]);

  const handleCountrySelect = (country) => {
    onSelect(country);
    onOpenChange(false);
    setSearchQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[80vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle>Select Country</DialogTitle>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search countries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[50vh] px-6 pb-6">
          <div className="space-y-1 mt-4">
            {filteredCountries.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountrySelect(country)}
                className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors ${
                  selectedCountry?.code === country.code ? "bg-accent" : ""
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                  <span className="font-medium">{country.name}</span>
                  {useIso3 && (
                    <span className="text-xs text-muted-foreground">
                      ({country.iso3})
                    </span>
                  )}
                </div>
                {showMobileCode && (
                  <span className="text-sm text-muted-foreground">
                    {country.mobileCode}
                  </span>
                )}
                {!showMobileCode && useIso3 && (
                  <span className="text-sm font-medium text-muted-foreground">
                    {country.iso3}
                  </span>
                )}
              </button>
            ))}
            {filteredCountries.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No countries found
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getFlagEmoji(countryCode) {
  const flags = {
    AU: "🇦🇺",
    CA: "🇨🇦",
    KH: "🇰🇭",
    CN: "🇨🇳",
    FR: "🇫🇷",
    DE: "🇩🇪",
    HK: "🇭🇰",
    ID: "🇮🇩",
    JP: "🇯🇵",
    MY: "🇲🇾",
    NZ: "🇳🇿",
    PH: "🇵🇭",
    SG: "🇸🇬",
    KR: "🇰🇷",
    ES: "🇪🇸",
    TW: "🇹🇼",
    TH: "🇹🇭",
    GB: "🇬🇧",
    US: "🇺🇸",
  };
  return flags[countryCode] || "🏳️";
}

export { countries };
